import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Rnd } from 'react-rnd';
import { useAxiosClient } from '../utils/useAxiosClient';
import PostCard from '../components/PostCard';

// Available orgs for testing
const TEST_ORGS = [
  { id: 158, name: 'Berry College' },
  { id: 177, name: 'Carolina Mejia' },
];

/**
 * Parse post blocks from text
 * Detects patterns like:
 * [POST brand="X" platform="Y" date="Z" engagement="N" url="..."]Message[/POST]
 */
interface ParsedPost {
  brand: string;
  platform: string;
  message: string;
  date?: string;
  engagement?: string;
  url?: string;
}

const parsePostBlocks = (text: string): { posts: ParsedPost[]; cleanedText: string } => {
  const posts: ParsedPost[] = [];
  const postRegex = /\[POST\s+([^\]]+)\]([\s\S]*?)\[\/POST\]/g;
  
  let cleanedText = text.replace(postRegex, (match, attributes, message) => {
    const post: ParsedPost = {
      brand: '',
      platform: '',
      message: message.trim(),
    };
    
    // Parse attributes
    const attrRegex = /(\w+)="([^"]*)"/g;
    let attrMatch;
    while ((attrMatch = attrRegex.exec(attributes)) !== null) {
      const [, key, value] = attrMatch;
      if (key === 'brand') post.brand = value;
      else if (key === 'platform') post.platform = value;
      else if (key === 'date') post.date = value;
      else if (key === 'engagement') post.engagement = value;
      else if (key === 'url') post.url = value;
    }
    
    if (post.brand && post.platform) {
      posts.push(post);
      return `__POST_${posts.length - 1}__`;
    }
    return match;
  });
  
  return { posts, cleanedText };
};

/**
 * Simple markdown formatter for chat messages
 * Supports: **bold**, *italic*, `code`, ```code blocks```, headers, lists, [POST] blocks
 */
const formatMarkdown = (text: string): React.ReactNode => {
  // First, extract POST blocks
  const { posts, cleanedText } = parsePostBlocks(text);
  
  // Split by code blocks first (preserve them)
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  const codeBlocks: { placeholder: string; lang: string; code: string }[] = [];
  let processedText = cleanedText.replace(codeBlockRegex, (match, lang, code) => {
    const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
    codeBlocks.push({ placeholder, lang: lang || '', code: code.trim() });
    return placeholder;
  });

  // Process inline formatting
  const formatInline = (line: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    // Match patterns: **bold**, *italic*, `code`
    const inlineRegex = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`)/g;
    let lastIdx = 0;
    let inlineMatch;
    let inlineKey = 0;

    while ((inlineMatch = inlineRegex.exec(line)) !== null) {
      // Add text before match
      if (inlineMatch.index > lastIdx) {
        result.push(line.slice(lastIdx, inlineMatch.index));
      }

      if (inlineMatch[2]) {
        // **bold**
        result.push(<strong key={`b-${inlineKey++}`}>{inlineMatch[2]}</strong>);
      } else if (inlineMatch[3]) {
        // *italic*
        result.push(<em key={`i-${inlineKey++}`}>{inlineMatch[3]}</em>);
      } else if (inlineMatch[4]) {
        // `code`
        result.push(
          <code
            key={`c-${inlineKey++}`}
            style={{
              backgroundColor: 'rgba(176, 38, 255, 0.2)',
              padding: '2px 6px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.85em',
            }}
          >
            {inlineMatch[4]}
          </code>
        );
      }

      lastIdx = inlineMatch.index + inlineMatch[0].length;
    }

    // Add remaining text
    if (lastIdx < line.length) {
      result.push(line.slice(lastIdx));
    }

    return result.length > 0 ? result : [line];
  };

  // Split by lines and process
  const lines = processedText.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let inList = false;

  lines.forEach((line, lineIndex) => {
    // Check for POST placeholder
    const postMatch = line.match(/__POST_(\d+)__/);
    if (postMatch) {
      // Flush list if active
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      
      const postIndex = parseInt(postMatch[1], 10);
      const post = posts[postIndex];
      if (post) {
        elements.push(
          <PostCard
            key={`post-${lineIndex}`}
            brand={post.brand}
            platform={post.platform}
            message={post.message}
            date={post.date}
            engagementScore={post.engagement}
            url={post.url}
          />
        );
      }
      return;
    }
    
    // Check for code block placeholder
    const codeBlockMatch = codeBlocks.find((cb) => line.includes(cb.placeholder));
    if (codeBlockMatch) {
      // Flush list if active
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }

      elements.push(
        <pre
          key={`pre-${lineIndex}`}
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            padding: '12px',
            borderRadius: '6px',
            overflow: 'auto',
            margin: '8px 0',
            border: '1px solid rgba(176, 38, 255, 0.2)',
          }}
        >
          <code
            style={{
              fontFamily: 'monospace',
              fontSize: '0.85em',
              color: '#e0e0e0',
            }}
          >
            {codeBlockMatch.code}
          </code>
        </pre>
      );
      return;
    }

    // Headers
    if (line.startsWith('### ')) {
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      elements.push(
        <div key={`h3-${lineIndex}`} style={{ fontWeight: 600, fontSize: '1em', marginTop: '12px', marginBottom: '4px' }}>
          {formatInline(line.slice(4))}
        </div>
      );
      return;
    }
    if (line.startsWith('## ')) {
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      elements.push(
        <div key={`h2-${lineIndex}`} style={{ fontWeight: 700, fontSize: '1.1em', marginTop: '12px', marginBottom: '4px' }}>
          {formatInline(line.slice(3))}
        </div>
      );
      return;
    }
    if (line.startsWith('# ')) {
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      elements.push(
        <div key={`h1-${lineIndex}`} style={{ fontWeight: 700, fontSize: '1.2em', marginTop: '12px', marginBottom: '4px' }}>
          {formatInline(line.slice(2))}
        </div>
      );
      return;
    }

    // List items
    if (line.match(/^[\-\*]\s/)) {
      inList = true;
      listItems.push(<li key={`li-${lineIndex}`}>{formatInline(line.slice(2))}</li>);
      return;
    }

    // Numbered list items
    if (line.match(/^\d+\.\s/)) {
      if (inList && listItems.length > 0 && !line.match(/^\d+\.\s/)) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
      }
      inList = true;
      const content = line.replace(/^\d+\.\s/, '');
      listItems.push(<li key={`li-${lineIndex}`}>{formatInline(content)}</li>);
      return;
    }

    // Flush list before adding regular content
    if (inList && listItems.length > 0) {
      elements.push(
        <ol key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
          {listItems}
        </ol>
      );
      listItems = [];
      inList = false;
    }

    // Regular line
    if (line.trim()) {
      elements.push(
        <div key={`p-${lineIndex}`} style={{ marginBottom: '4px' }}>
          {formatInline(line)}
        </div>
      );
    } else if (lineIndex > 0 && lineIndex < lines.length - 1) {
      // Empty line - add spacing
      elements.push(<div key={`br-${lineIndex}`} style={{ height: '8px' }} />);
    }
  });

  // Flush remaining list items
  if (listItems.length > 0) {
    elements.push(
      <ul key="list-final" style={{ margin: '8px 0', paddingLeft: '20px' }}>
        {listItems}
      </ul>
    );
  }

  return <>{elements}</>;
};

/**
 * ChatTest - Floating chatbot test page for IL-2
 * 
 * Features:
 * - Floating, draggable, resizable chatbox
 * - Lorem ipsum background (simulates dashboard)
 * - Position/size persisted to localStorage
 * - No auth required (bypasses Auth0)
 * - Selectable org_id via buttons (158 Higher Ed, 177 Piedmont)
 * 
 * Access via: /chat-test (direct URL, not in nav menu)
 */

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatPosition {
  x: number;
  y: number;
}

interface ChatSize {
  width: number;
  height: number;
}

const STORAGE_KEY_POSITION = 'chatbox-position';
const STORAGE_KEY_SIZE = 'chatbox-size';
const DEFAULT_SIZE = { width: 500, height: 600 };
const MIN_SIZE = { width: 450, height: 400 };
const MAX_SIZE = { width: 800, height: 900 };

const ChatTest: React.FC = () => {
  const [orgId, setOrgId] = useState(158); // Default to Higher Ed
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const axiosClient = useAxiosClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Autoscroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  // Calculate default position (bottom-right with 20px margin)
  const getDefaultPosition = (): ChatPosition => {
    const margin = 20;
    return {
      x: window.innerWidth - DEFAULT_SIZE.width - margin,
      y: window.innerHeight - DEFAULT_SIZE.height - margin,
    };
  };

  // Chatbox position and size state with localStorage persistence
  const [position, setPosition] = useState<ChatPosition>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_POSITION);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return getDefaultPosition();
      }
    }
    return getDefaultPosition();
  });

  const [size, setSize] = useState<ChatSize>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_SIZE);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_SIZE;
      }
    }
    return DEFAULT_SIZE;
  });

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_POSITION, JSON.stringify(position));
  }, [position]);

  // Save size to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SIZE, JSON.stringify(size));
  }, [size]);

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // TODO: Phase 1 - Replace with actual endpoint
      const response = await axiosClient.post('/api/chat/test', {
        org_id: orgId,
        message: userMessage.content,
      });

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.data.response || 'No response received',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: '❌ Error: Could not connect to backend. Make sure the /api/chat/test endpoint is implemented.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        background: 'linear-gradient(135deg, #111111 0%, #1a1a1a 100%)',
        overflow: 'auto',
        padding: 4,
      }}
    >
      {/* Lorem Ipsum Background Content (Simulates Dashboard) */}
      <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Typography variant="h3" sx={{ color: '#B026FF', fontWeight: 700, marginBottom: 3 }}>
          InkLantern Dashboard
        </Typography>
        
        <Paper sx={{ padding: 3, marginBottom: 3, background: 'rgba(255, 255, 255, 0.05)' }}>
          <Typography variant="h5" sx={{ color: '#ffffff', marginBottom: 2 }}>
            Brand Performance Overview
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.8 }}>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. 
            Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. 
            Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
          </Typography>
        </Paper>

        <Paper sx={{ padding: 3, marginBottom: 3, background: 'rgba(255, 255, 255, 0.05)' }}>
          <Typography variant="h5" sx={{ color: '#ffffff', marginBottom: 2 }}>
            Engagement Metrics
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.8 }}>
            Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. 
            Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, 
            totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.
          </Typography>
        </Paper>

        <Paper sx={{ padding: 3, marginBottom: 3, background: 'rgba(255, 255, 255, 0.05)' }}>
          <Typography variant="h5" sx={{ color: '#ffffff', marginBottom: 2 }}>
            Top Performing Content
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.8 }}>
            Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui 
            ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet, consectetur, 
            adipisci velit, sed quia non numquam eius modi tempora incidunt ut labore et dolore magnam aliquam quaerat voluptatem.
          </Typography>
        </Paper>

        <Paper sx={{ padding: 3, marginBottom: 3, background: 'rgba(255, 255, 255, 0.05)' }}>
          <Typography variant="h5" sx={{ color: '#ffffff', marginBottom: 2 }}>
            Analytics Summary
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.8 }}>
            Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi 
            consequatur? Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, 
            vel illum qui dolorem eum fugiat quo voluptas nulla pariatur?
          </Typography>
        </Paper>
      </Box>

      {/* Floating Chatbox with react-rnd */}
      <Rnd
        size={size}
        position={position}
        onDragStop={(e, d) => {
          setPosition({ x: d.x, y: d.y });
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          setSize({
            width: parseInt(ref.style.width),
            height: parseInt(ref.style.height),
          });
          setPosition(position);
        }}
        minWidth={MIN_SIZE.width}
        minHeight={MIN_SIZE.height}
        maxWidth={MAX_SIZE.width}
        maxHeight={MAX_SIZE.height}
        bounds="window"
        dragHandleClassName="chat-drag-handle"
        style={{
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.98) 0%, rgba(26, 26, 26, 0.98) 100%)',
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(176, 38, 255, 0.3), 0 0 0 1px rgba(176, 38, 255, 0.5)',
            overflow: 'hidden',
          }}
        >
          {/* Draggable Header */}
          <Box
            className="chat-drag-handle"
            sx={{
              padding: 1.5,
              background: 'linear-gradient(135deg, rgba(176, 38, 255, 0.2) 0%, rgba(155, 0, 230, 0.2) 100%)',
              borderBottom: '1px solid rgba(176, 38, 255, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              cursor: 'move',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DragIndicatorIcon sx={{ color: 'rgba(176, 38, 255, 0.7)' }} />
                <Typography variant="h6" sx={{ color: '#B026FF', fontWeight: 600 }}>
                  💬 IL-2 Chatbot
                </Typography>
              </Box>
              <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            
            {/* Org Selector */}
            <ButtonGroup size="small" sx={{ alignSelf: 'flex-start' }}>
              {TEST_ORGS.map((org) => (
                <Button
                  key={org.id}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent drag
                    setOrgId(org.id);
                    setMessages([]); // Clear chat on org change
                  }}
                  sx={{
                    backgroundColor: orgId === org.id ? '#B026FF' : 'transparent',
                    color: orgId === org.id ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(176, 38, 255, 0.5)',
                    fontSize: '0.7rem',
                    py: 0.25,
                    px: 1,
                    '&:hover': {
                      backgroundColor: orgId === org.id ? '#C24FFF' : 'rgba(176, 38, 255, 0.2)',
                      borderColor: 'rgba(176, 38, 255, 0.7)',
                    },
                  }}
                >
                  {org.id}: {org.name}
                </Button>
              ))}
            </ButtonGroup>
          </Box>

          {/* Chat Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'rgba(255, 255, 255, 0.5)',
                  textAlign: 'center',
                  padding: 2,
                }}
              >
                <Typography variant="body2">
                  Start a conversation to test the chatbot...<br />
                  <span style={{ fontSize: '0.85em', opacity: 0.7 }}>
                    (Org ID: {orgId} | No Auth)
                  </span>
                </Typography>
              </Box>
            ) : (
              messages.map((message, index) => (
                <Paper
                  key={index}
                  elevation={2}
                  sx={{
                    padding: 1.5,
                    alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    background:
                      message.role === 'user'
                        ? 'linear-gradient(135deg, #B026FF 0%, #9B00E6 100%)'
                        : 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, marginBottom: 0.5, opacity: 0.8, display: 'block' }}>
                    {message.role === 'user' ? 'You' : 'Assistant'}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      wordBreak: 'break-word',
                      '& ul, & ol': {
                        margin: '8px 0',
                        paddingLeft: '20px',
                      },
                      '& li': {
                        marginBottom: '4px',
                      },
                      '& strong': {
                        fontWeight: 700,
                      },
                      '& em': {
                        fontStyle: 'italic',
                      },
                    }}
                  >
                    {message.role === 'assistant' ? formatMarkdown(message.content) : message.content}
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', marginTop: 0.5 }}>
                    {message.timestamp.toLocaleTimeString()}
                  </Typography>
                </Paper>
              ))
            )}

            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#B026FF' }}>
                <CircularProgress size={16} color="inherit" />
                <Typography variant="caption">Assistant is thinking...</Typography>
              </Box>
            )}
            
            {/* Scroll anchor for autoscroll */}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              padding: 2,
              background: 'rgba(0, 0, 0, 0.5)',
              borderTop: '1px solid rgba(176, 38, 255, 0.2)',
              display: 'flex',
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              disabled={loading}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#ffffff',
                  fontSize: '0.9rem',
                  '& fieldset': {
                    borderColor: 'rgba(176, 38, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(176, 38, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#B026FF',
                  },
                },
                '& .MuiInputBase-input': {
                  color: '#ffffff',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!input.trim() || loading}
              size="small"
              sx={{
                backgroundColor: '#B026FF',
                minWidth: '70px',
                '&:hover': {
                  backgroundColor: '#C24FFF',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(176, 38, 255, 0.3)',
                },
              }}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Rnd>
    </Box>
  );
};

export default ChatTest;

